import { faRefresh, faSuitcase } from "@fortawesome/pro-regular-svg-icons";
import { Navigate, useSearchParams } from "react-router-dom";

import { Pill } from "src/base-components/Pill";
import { useCasesForEntity } from "src/cases/queries/cases";
import { GetCasesByEntityApiV1CasesRelatedGetFilterEnvEnum } from "src/clients/case-management";
import { DecisionsTabContent } from "src/entities/entityView/DecisionsTabContent";
import { CasesTabContent } from "src/entities/entityView/EntityCases";
import { EventsTabContent } from "src/entities/entityView/EventsTabContent";
import { Features } from "src/entities/entityView/Features";
import { RelatedEntities } from "src/entities/entityView/RelatedEntities";
import { CASES_SORT_PARAM } from "src/entities/entityView/useCasesSortParam";
import {
  EntityTabIds,
  useEntityTabRefresh,
} from "src/entities/entityView/useEntityTabRefresh";
import { ExtendedHistoricalDecisionApiResponse } from "src/flow/decisionHistory/Columns";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import {
  useWorkspaceFeatureGates,
  WorkspaceFeatureGates,
} from "src/hooks/useWorkspaceFeatureGates";
import {
  SubHeader,
  SubHeaderButton,
  SubHeaderTab,
  SubHeaderTabs,
} from "src/layout/SubHeader";
import { EntityViewParams, getUrlToEntityDetailPage } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  selectedDecision: ExtendedHistoricalDecisionApiResponse | null;
  onSelectDecision: (
    decision: ExtendedHistoricalDecisionApiResponse | null,
  ) => void;
  tab: EntityTabIds;
};

const tabLabels = {
  [EntityTabIds.Cases]: "Cases",
  [EntityTabIds.Decisions]: "Decisions",
  [EntityTabIds.Features]: "Features",
  [EntityTabIds.Events]: "Events",
  [EntityTabIds.RelatedEntities]: "Related Entities",
};

type TabDefinition = {
  id: EntityTabIds;
  label: string;
  requiredGate: keyof WorkspaceFeatureGates | null;
  rightSlot?: React.ReactNode;
};

type CasesCountPillProps = {
  entityId: string;
  entityType: string;
  environment: GetCasesByEntityApiV1CasesRelatedGetFilterEnvEnum;
};

const CasesCountPill: React.FC<CasesCountPillProps> = ({
  entityId,
  entityType,
  environment,
}) => {
  const { data: cases } = useCasesForEntity({
    entityId,
    entityType,
    environment,
  });
  const casesCount = cases?.length ?? 0;

  if (casesCount === 0) {
    return null;
  }

  return (
    <Pill size="sm" variant="indigo-light">
      <Pill.Icon icon={faSuitcase} />
      <Pill.Text>{casesCount > 99 ? "99+" : casesCount}</Pill.Text>
    </Pill>
  );
};

export const EntityTabs: React.FC<Props> = ({
  selectedDecision,
  onSelectDecision,
  tab,
}) => {
  const { refresh, isRefreshing } = useEntityTabRefresh(tab);
  const {
    orgId,
    wsId,
    schema,
    id: entityId,
  } = useParamsDecode<EntityViewParams>();
  const featureGates = useWorkspaceFeatureGates();
  const capabilities = useCapabilities();
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();
  const [environment] = useEnvironment();
  const [searchParams] = useSearchParams();

  const casesSort = searchParams.get(CASES_SORT_PARAM);
  const preservedParams = casesSort
    ? { [CASES_SORT_PARAM]: casesSort }
    : undefined;

  const showCasesTab = isCaseManagementEnabled && capabilities.cases.canAccess;

  const tabDefinitions: TabDefinition[] = [
    ...(showCasesTab
      ? [
          {
            id: EntityTabIds.Cases,
            label: tabLabels[EntityTabIds.Cases],
            requiredGate: null,
            rightSlot: (
              <CasesCountPill
                entityId={entityId}
                entityType={schema}
                environment={
                  environment as GetCasesByEntityApiV1CasesRelatedGetFilterEnvEnum
                }
              />
            ),
          },
        ]
      : []),
    {
      id: EntityTabIds.Decisions,
      label: tabLabels[EntityTabIds.Decisions],
      requiredGate: null,
    },
    {
      id: EntityTabIds.Events,
      label: tabLabels[EntityTabIds.Events],
      requiredGate: "featuresEventsEnabled",
    },
    {
      id: EntityTabIds.Features,
      label: tabLabels[EntityTabIds.Features],
      requiredGate: "featuresEventsEnabled",
    },
    {
      id: EntityTabIds.RelatedEntities,
      label: tabLabels[EntityTabIds.RelatedEntities],
      requiredGate: null,
    },
  ];

  // The /cases route stays mounted for every tab, so a user without cases
  // access can land on it from an old or shared link: send them to the default
  // tab instead of rendering cases data the tab bar no longer points to.
  if (tab === EntityTabIds.Cases && !showCasesTab) {
    return (
      <Navigate
        to={getUrlToEntityDetailPage(
          orgId,
          wsId,
          schema,
          entityId,
          EntityTabIds.Decisions,
        )}
        replace
      />
    );
  }

  const content = {
    [EntityTabIds.Cases]: <CasesTabContent />,
    [EntityTabIds.Decisions]: (
      <DecisionsTabContent
        selectedDecision={selectedDecision}
        onSelectDecision={onSelectDecision}
      />
    ),
    [EntityTabIds.Features]: <Features />,
    [EntityTabIds.Events]: <EventsTabContent />,
    [EntityTabIds.RelatedEntities]: <RelatedEntities />,
  };

  return (
    <div className="w-full overflow-x-hidden">
      <SubHeader
        leftSlot={
          <SubHeaderTabs dataLoc="entity-tabs">
            {tabDefinitions.map(({ id, label, requiredGate, rightSlot }) => {
              if (requiredGate && !featureGates[requiredGate]) {
                return null;
              }

              return (
                <SubHeaderTab
                  key={id}
                  rightSlot={rightSlot}
                  to={getUrlToEntityDetailPage(
                    orgId,
                    wsId,
                    schema,
                    entityId,
                    id,
                    undefined,
                    preservedParams,
                  )}
                >
                  {label}
                </SubHeaderTab>
              );
            })}
          </SubHeaderTabs>
        }
      >
        <SubHeaderButton
          dataLoc="entity-tabs-refresh"
          icon={faRefresh}
          spin={isRefreshing}
          tooltip="Refresh"
          onClick={refresh}
        />
      </SubHeader>
      {content[tab]}
    </div>
  );
};
